import { RouteRecordRaw, RouteLocationNormalized } from "vue-router";
import { addRoutes } from "@/core/router";
import {
  registerNavMenuItem,
  NavMenuItem,
  addBreadCrumbs,
  BreadcrumbParam,
} from "@/core/layout";
import { getFlow, isNewUiEnabledForTenant } from "@/flow/services/flow.service";
import { getTenantId } from "@/core/api";

async function resolveEditFlowRouteName(
  to: RouteLocationNormalized
): Promise<"editFlows" | "editFlowsRouteNew"> {
  const id = to.params.id as string;
  const version = to.params.version as string;

  const flow = await getFlow(id, version);

  const shouldUseNewUi = !!flow?.newUi;

  return shouldUseNewUi ? "editFlowsRouteNew" : "editFlows";
}

function makeEditFlowGuard(selfName: "editFlows" | "editFlowsRouteNew") {
  return async (to: RouteLocationNormalized, from: any, next: any) => {
    try {
      const targetName = await resolveEditFlowRouteName(to);

      if (targetName === selfName) {
        next();
        return;
      }

      next({
        name: targetName,
        params: to.params,
        query: to.query,
        hash: to.hash,
        replace: true,
      });
    } catch (error) {
      console.error("edit flow route resolution failed:", error);
      if (selfName === "editFlows") {
        next();
      } else {
        next({
          name: "editFlows",
          params: to.params,
          query: to.query,
          hash: to.hash,
          replace: true,
        });
      }
    }
  };
}

const flowsRoute: RouteRecordRaw = {
  path: "/flows",
  name: "flows",
  component: () => import("@/flow/pages/Flows.vue"),
  meta: {
    authorize: ["FLOWS.LIST.VIEW"],
  },
};
const createFlowsRoute: RouteRecordRaw = {
  path: "/flows/create",
  name: "createFlows",
  meta: {
    authorize: ["FLOWS.LIST.CREATE"],
  },
  component: () => import("@/flow/pages/EditFlow.vue"),
  beforeEnter: async (to, from, next) => {
    try {
      const tenantId = getTenantId();
      const enabled = await isNewUiEnabledForTenant(tenantId);
      if (enabled) {
        next({
          name: "createFlowsNew",
        });
      } else {
        next();
      }
    } catch (error) {
      console.error("ui.enable-new-ui check failed in beforeEnter:", error);
      next();
    }
  },
};

const createFlowsNewRoute: RouteRecordRaw = {
  path: "/flows/new-ui",
  name: "createFlowsNew",
  meta: {
    authorize: ["FLOWS.LIST.CREATE"],
  },
  component: () => import("@/flow/pages/EditVueFlow.vue"),
};

const editFlowsRoute: RouteRecordRaw = {
  path: "/flows/edit/:id/:version",
  name: "editFlows",
  component: () => import("@/flow/pages/EditFlow.vue"),
  meta: {
    authorize: ["FLOWS.LIST.EDIT"],
  },
  beforeEnter: makeEditFlowGuard("editFlows"),
};

const editFlowsRouteNew: RouteRecordRaw = {
  path: "/flows/edit/:id/:version",
  name: "editFlowsRouteNew",
  component: () => import("@/flow/pages/EditVueFlow.vue"),
  meta: {
    authorize: ["FLOWS.LIST.EDIT"],
  },
  beforeEnter: makeEditFlowGuard("editFlowsRouteNew"),
};

const exportFlowRoute: RouteRecordRaw = {
  path: "/flows/export/:id/:version",
  name: "exportFlows",
  component: () => import("@/flow/pages/ExportWizard.vue"),
  meta: {
    authorize: ["FLOWS.LIST.EXPORT"],
  },
};
addRoutes([
  flowsRoute,
  createFlowsRoute,
  createFlowsNewRoute,
  editFlowsRoute,
  editFlowsRouteNew,
  exportFlowRoute,
]);

const flowItem: NavMenuItem = {
  iconName: "network-wired",
  label: "Flows",
  name: "flows",
  targetRoute: {
    path: "/flows",
    name: "flows",
    component: () => import("@/flow/pages/Flows.vue"),
  },
  rank: 90,
  authority: "FLOWS.LIST.VIEW",
  isParent: false,
  hasParent: false,
};
registerNavMenuItem(flowItem);

const flowsBreadcrumb: BreadcrumbParam = {
  routeName: "flows",
  items: [
    {
      labelKey: "Auth.Home",
      route: "/dashboard",
      disabled: false,
      isTranslated: true,
      uniqueLabel: "",
    },
    {
      labelKey: "Flow.Flows",
      route: "/flows",
      disabled: true,
      isTranslated: true,
      uniqueLabel: "",
    },
  ],
};

const createFlowBreadCrumb: BreadcrumbParam = {
  routeName: "createFlows",
  items: [
    {
      labelKey: "Auth.Home",
      route: "/dashboard",
      disabled: false,
      isTranslated: true,
      uniqueLabel: "",
    },
    {
      labelKey: "Flow.Flows",
      route: "/flows",
      disabled: false,
      isTranslated: true,
      uniqueLabel: "",
    },
    {
      labelKey: "Common.Create",
      route: "",
      disabled: true,
      isTranslated: true,
      uniqueLabel: "",
    },
  ],
};

const editFlowBreadCrumb: BreadcrumbParam = {
  routeName: "editFlows",
  items: [
    {
      labelKey: "Auth.Home",
      route: "/dashboard",
      disabled: false,
      isTranslated: true,
      uniqueLabel: "",
    },
    {
      labelKey: "Flow.Flows",
      route: "/flows",
      disabled: false,
      isTranslated: true,
      uniqueLabel: "",
    },
    {
      labelKey: "Common.Edit",
      route: "",
      disabled: true,
      isTranslated: false,
      uniqueLabel: "",
    },
  ],
};

const exportFlowBreadcrumb: BreadcrumbParam = {
  routeName: "exportFlows",
  items: [
    {
      labelKey: "Auth.Home",
      route: "/dashboard",
      disabled: false,
      isTranslated: true,
      uniqueLabel: "",
    },
    {
      labelKey: "Flow.Flows",
      route: "/flows",
      disabled: false,
      isTranslated: true,
      uniqueLabel: "",
    },
    {
      labelKey: "Flow.Export",
      route: "",
      disabled: true,
      isTranslated: true,
      uniqueLabel: "",
    },
  ],
};

addBreadCrumbs([
  flowsBreadcrumb,
  createFlowBreadCrumb,
  editFlowBreadCrumb,
  exportFlowBreadcrumb,
]);
